/**
 * TimelineContainer Component
 *
 * Main orchestrator component that:
 * - Provides TimelineContext for synchronized zoom/pan
 * - Manages display mode (single/dual/toggle)
 * - Handles keyboard navigation
 * - Renders controls and timeline panels
 */

import { useRef, useState, useCallback, useMemo, useEffect, type CSSProperties } from 'react'
import './TimelineContainer.css'
import { TimelineProvider, useTimelineContext } from './context/TimelineContext'
import { TimelineControls } from './components/TimelineControls'
import { DualTimelinePanel } from './components/DualTimelinePanel'
import { TimelineViewport } from './components/TimelineViewport'
import { TimelineAxis } from './components/TimelineAxis'
import { useTimelineDomain, mergeDomains } from './hooks/useTimelineDomain'
import { useKeyboardNavigation, getKeyboardNavigationAriaLabel } from './hooks/useKeyboardNavigation'
import type {
  TimelineContainerProps,
  TimelineDisplayMode,
} from './types'
import { DEFAULT_ZOOM_CONSTRAINTS } from './context/types'

/** Default timeline row height */
const DEFAULT_TIMELINE_HEIGHT = 48

/** Default pan step for keyboard navigation */
const PAN_STEP = 50

/**
 * Inner component that uses the context
 */
function TimelineContainerInner({
  data,
  width,
  timelineHeight = DEFAULT_TIMELINE_HEIGHT,
  showControls = true,
  onSegmentClick,
  selectedSegmentUid,
  hoveredSegmentUid,
  onSegmentHover,
  showBackgroundSegments = false,
  className,
  displayMode: propDisplayMode,
  defaultExpanded = true,
  expanded: controlledExpanded,
  onExpandedChange,
  animationDuration = 250,
  collapsedStatusMessage,
}: Omit<TimelineContainerProps, 'zoomConstraints'> & { containerWidth: number }) {
  const containerRef = useRef<HTMLDivElement>(null)
  const {
    state,
    zoomIn,
    zoomOut,
    reset,
    pan,
    canZoomIn,
    canZoomOut,
  } = useTimelineContext()

  // Determine display mode based on data
  const displayMode: TimelineDisplayMode = useMemo(() => {
    if (propDisplayMode) return propDisplayMode
    // Auto-detect: if before data exists and has segments, default to dual
    if (data.before && data.before.segments.length > 0) {
      return 'dual'
    }
    return 'single'
  }, [propDisplayMode, data.before])

  // Keyboard navigation handlers
  const handlePanLeft = useCallback(() => pan(-PAN_STEP), [pan])
  const handlePanRight = useCallback(() => pan(PAN_STEP), [pan])

  // Setup keyboard navigation
  useKeyboardNavigation({
    containerRef,
    onZoomIn: zoomIn,
    onZoomOut: zoomOut,
    onPanLeft: handlePanLeft,
    onPanRight: handlePanRight,
    onReset: reset,
    enabled: true,
    panStep: PAN_STEP,
  })

  // Container styles
  const containerStyle: CSSProperties = useMemo(
    () => ({
      width: typeof width === 'number' ? `${width}px` : width || '100%',
    }),
    [width]
  )

  const containerWidth =
    typeof width === 'number' ? width : containerRef.current?.offsetWidth || 800

  return (
    <div
      ref={containerRef}
      className={`timeline-container ${className || ''}`}
      style={containerStyle}
      tabIndex={0}
      role="application"
      aria-label={getKeyboardNavigationAriaLabel()}
    >
      {/* Controls row */}
      {showControls && (
        <div className="timeline-container__controls">
          <TimelineControls
            onZoomIn={zoomIn}
            onZoomOut={zoomOut}
            onReset={reset}
            canZoomIn={canZoomIn}
            canZoomOut={canZoomOut}
          />
        </div>
      )}

      {/* Timeline content */}
      <div className="timeline-container__content">
        {displayMode === 'single' ? (
          // Single timeline mode
          <>
            <TimelineViewport
              segments={data.after.segments}
              backgroundSegments={data.after.backgroundSegments}
              label="Timeline"
              height={timelineHeight}
              showBackgroundSegments={showBackgroundSegments}
              onSegmentClick={onSegmentClick}
              selectedSegmentUid={selectedSegmentUid}
              hoveredSegmentUid={hoveredSegmentUid}
              onSegmentHover={onSegmentHover}
              domain={state.domain}
              containerWidth={containerWidth}
            />
            <TimelineAxis
              minDate={state.domain.minDate}
              maxDate={state.domain.maxDate}
              containerWidth={containerWidth}
              transform={state.transform}
            />
          </>
        ) : (
          // Dual or toggle mode
          <DualTimelinePanel
            beforeData={data.before || { segments: [], label: 'BEFORE' }}
            afterData={data.after}
            newRecord={data.newRecord}
            width={containerWidth}
            timelineHeight={timelineHeight}
            gap={8}
            onSegmentClick={onSegmentClick}
            selectedSegmentUid={selectedSegmentUid}
            hoveredSegmentUid={hoveredSegmentUid}
            onSegmentHover={onSegmentHover}
            showBackgroundSegments={showBackgroundSegments}
            displayMode={displayMode}
            expanded={controlledExpanded}
            defaultExpanded={defaultExpanded}
            onExpandedChange={onExpandedChange}
            animationDuration={animationDuration}
            domain={state.domain}
            collapsedStatusMessage={collapsedStatusMessage}
          />
        )}
      </div>
    </div>
  )
}

/**
 * TimelineContainer Component
 *
 * Main entry point that wraps everything in the TimelineProvider.
 */
export function TimelineContainer({
  data,
  width,
  timelineHeight = DEFAULT_TIMELINE_HEIGHT,
  showControls = true,
  zoomConstraints,
  onSegmentClick,
  selectedSegmentUid,
  hoveredSegmentUid,
  onSegmentHover,
  showBackgroundSegments = false,
  className,
  displayMode,
  defaultExpanded = true,
  expanded,
  onExpandedChange,
  animationDuration = 250,
  collapsedStatusMessage,
}: TimelineContainerProps) {
  const containerRef = useRef<HTMLDivElement>(null)
  const [containerWidth, setContainerWidth] = useState(800)

  // Measure container width
  useEffect(() => {
    const updateWidth = () => {
      if (containerRef.current) {
        const measured = containerRef.current.offsetWidth
        if (measured > 0) {
          setContainerWidth(measured)
        }
      }
    }

    updateWidth()

    // Also update on resize
    const observer = new ResizeObserver(updateWidth)
    if (containerRef.current) {
      observer.observe(containerRef.current)
    }

    return () => observer.disconnect()
  }, [])

  // Calculate domain from all segments
  const beforeDomain = useTimelineDomain({
    segments: data.before?.segments || [],
    additionalSegments: data.before?.backgroundSegments,
  })

  const afterDomain = useTimelineDomain({
    segments: data.after.segments,
    additionalSegments: data.after.backgroundSegments,
  })

  const combinedDomain = useMemo(
    () => mergeDomains(beforeDomain, afterDomain),
    [beforeDomain, afterDomain]
  )

  // Merge constraints with defaults
  const mergedConstraints = useMemo(
    () => ({ ...DEFAULT_ZOOM_CONSTRAINTS, ...zoomConstraints }),
    [zoomConstraints]
  )

  return (
    <div
      ref={containerRef}
      style={{ width: typeof width === 'number' ? `${width}px` : width || '100%' }}
    >
      <TimelineProvider
        containerWidth={containerWidth}
        constraints={mergedConstraints}
        initialDomain={combinedDomain}
      >
        <TimelineContainerInner
          data={data}
          width={width}
          containerWidth={containerWidth}
          timelineHeight={timelineHeight}
          showControls={showControls}
          onSegmentClick={onSegmentClick}
          selectedSegmentUid={selectedSegmentUid}
          hoveredSegmentUid={hoveredSegmentUid}
          onSegmentHover={onSegmentHover}
          showBackgroundSegments={showBackgroundSegments}
          className={className}
          displayMode={displayMode}
          defaultExpanded={defaultExpanded}
          expanded={expanded}
          onExpandedChange={onExpandedChange}
          animationDuration={animationDuration}
          collapsedStatusMessage={collapsedStatusMessage}
        />
      </TimelineProvider>
    </div>
  )
}

export default TimelineContainer
